import { bool, func, string } from 'prop-types';
import React, { forwardRef, useRef, useState } from 'react';
import { FaImage } from 'react-icons/fa';
import '../styles/ZoomableImage.css';

const widthsRegex = /_[0-9]{3}x$/;
const widths = [200, 300, 400, 500, 600, 700, 800];
const cdnDomain = new URL(process.env.RESOURCE_ATTACHMENTS_URL).hostname;

const ZoomableResponsiveImage = forwardRef(({
    hoverMagnify,
    imageUrl,
    imageAlt,
    className,
    placeholderClassName,
    mobileSize,
    desktopSize,
    onClick,
    viewer,
    lazyLoad,
}, ref) => {
    const containerRef = useRef(null);
    const [isZoomed, setZoomed] = useState(false);
    const [isViewerZoomed, setViewerZoomed] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(3);

    const handleMouseOver = () => {
    //   if (isZoomed) return;
        setZoomed(!isZoomed);
    };

    const handleClick = () => {
        if(hoverMagnify) 
            setZoomed(false);
        if(!hoverMagnify) 
            setViewerZoomed(!isViewerZoomed);
        handleMouseOver();
    };
    const handleMouseMove = (evt) => {
        if (!isZoomed) 
            return;

        const containerPos = containerRef.current.getBoundingClientRect();

        const percentageX = `${
            ((evt.clientX - containerPos.left) * 100) / containerPos.width
        }%`;
        const percentageY = `${
            ((evt.clientY - containerPos.top) * 100) / containerPos.height
        }%`;

        containerRef.current.style.setProperty('--zoom-pos-x', percentageX);
        containerRef.current.style.setProperty('--zoom-pos-y', percentageY);
    };

    const handleWheel = (evt) => {
        if (!isZoomed) 
            return;
        evt.preventDefault();

        const newZoom = zoomLevel + evt.deltaY * -0.01;
        const minZoom = 1.25;
        const maxZoom = 4;

        const clampedZoom = Math.max(Math.min(newZoom, maxZoom), minZoom);
        setZoomLevel(clampedZoom);
        containerRef.current.style.setProperty('--zoom', clampedZoom);
    };
    if (typeof imageUrl !== 'string'
        || !imageUrl.length) {
        return (
            <FaImage
                className={placeholderClassName ?? className}
            />
        );
    }
    const srcSet = [];
    if (imageUrl.lastIndexOf('.') !== -1
        && imageUrl.includes(cdnDomain)
        && imageUrl.match(widthsRegex) === null
        && process.env.RESPONSIVE_IMAGES === 'true') {
        const extensionIdx = imageUrl.lastIndexOf('.');
        for (const width of widths) {
            const widthImageUrl = `${imageUrl.slice(0, extensionIdx)
            }_${width}x${
                imageUrl.slice(extensionIdx)}`;
            srcSet.push(`${widthImageUrl} ${width}w`);
        }
    }
    return (
        <div
            ref={containerRef}
            className={`zoomable ${isZoomed ? 'zoomable--zoomed' : ''} ${viewer ? 'zoomable--viewer' : ''} ${isViewerZoomed ? 'zoomable--viewer-zoomed' : ''} `}
            // onMouseOver = {handleMouseMove}
            onMouseEnter={hoverMagnify ? handleMouseOver : null}
            onTouchStart={hoverMagnify ? handleMouseOver : null}
            onMouseLeave={hoverMagnify ? handleMouseOver : null}
            onTouchEnd={hoverMagnify ? handleMouseOver : null}
            onClick={handleClick}
            onMouseMove={handleMouseMove}
            onTouchMove={handleMouseMove}
            onWheel={handleWheel}
        >
            <img
                ref={ref}
                className={`${className} zoomable__img`}
                alt={imageAlt ?? ''}
                onClick={onClick}
                {...(!srcSet.length) ? ({
                    src: imageUrl,
                }) : ({
                    sizes: `(max-width: 768px) ${mobileSize},\n${
                        desktopSize}`,
                    srcSet: srcSet.join(', '),
                    'data-sizes': 'auto',
                    'data-widths': JSON.stringify(widths),
                    'data-srcset': srcSet.join(', '),
                })}
                {...lazyLoad === true && ({
                    loading: 'lazy',
                })}
            />
        </div>
       
    );
});

ZoomableResponsiveImage.propTypes = {
    viewer: bool,
    imageUrl: string,
    imageAlt: string,
    className: string,
    placeholderClassName: string,
    mobileSize: string,
    desktopSize: string,
    onClick: func,
    lazyLoad: bool,
    hoverMagnify: bool,
};

ZoomableResponsiveImage.defaultProps = {
    imageUrl: '',
    imageAlt: '',
    className: '',
    placeholderClassName: '',
    mobileSize: '200px',
    desktopSize: '200px',
    onClick: (e) => {},
    lazyLoad: true,
    hoverMagnify: false,
};

ZoomableResponsiveImage.displayName = 'ZoomableResponsiveImage';

export default ZoomableResponsiveImage;
